import { useNavigate } from '@tanstack/react-router'
import {
  ColumnFiltersState,
  FilterFn,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  RowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { normalizeString } from 'components/table/enhancements/filters/filterUtils'
import { TableComponent } from 'components/table/TableComponent'
import { compareAsc, parseISO } from 'date-fns'
import { WebGLContext } from 'features/assetDatabase/contexts/WebGLLayer'
import { useGetCustomSiteField } from 'features/assetDatabase/hooks/useGetCustomSideField'
import { AssetData } from 'features/assetDatabase/types/assetTableTypes'
import React, { useContext, useEffect, useMemo, useState } from 'react'

import AssetTableColumns from './AssetTableColumns'
import { getAssetTableCustomColumns } from './AssetTableHelpers'

interface AssetTableProps {
  siteID: string
  isLoading: boolean
  data: AssetData[]
  initialTableState?: {
    asset?: string
    lastInspection?: string
    lastUpdated?: string
    components?: string[]
    area?: string
    areaDescription?: string
    erpId?: string
    criticality?: string
  }
  onTableStateChange?: (newState: {
    asset?: string
    lastInspection?: string
    lastUpdated?: string
    components?: string[]
    area?: string
    areaDescription?: string
    erpId?: string
    criticality?: string
  }) => void
}

export const AssetTable: React.FC<AssetTableProps> = (props) => {
  const { siteID, isLoading, data, initialTableState, onTableStateChange } =
    props
  const navigate = useNavigate({ from: '/assets/$siteID/$unitID' })
  const { setAssetVizRenderingMap } = useContext(WebGLContext)

  const site = useGetCustomSiteField(siteID)
  const customFields = useMemo(() => {
    return site.data && site.data[0].entry.data.custom_fields
      ? site.data[0].entry.data.custom_fields
      : []
  }, [site.data])
  const customColumns = getAssetTableCustomColumns(customFields)
  const defaultColumns = AssetTableColumns(siteID)
  const columns = [...defaultColumns, ...customColumns]

  // Restore local sorting state for the table UI only
  const [sorting, setSorting] = React.useState<SortingState>([
    { id: 'lastUpdated', desc: true },
  ])

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 24,
  })

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

  // Initialize filters from URL
  useEffect(() => {
    if (initialTableState) {
      const filters = Object.entries(initialTableState)
        .filter(([key]) => {
          // Include standard filters
          const isStandardFilter = [
            'asset',
            'lastInspection',
            'lastUpdated',
            'components',
            'area',
            'areaDescription',
            'erpId',
            'criticality',
          ].includes(key)

          // Include custom field filters
          const isCustomField = customFields.some((field) => field.key === key)

          return isStandardFilter || isCustomField
        })
        .map(([id, value]) => ({
          id,
          value,
        }))
      setColumnFilters(filters)
    }
  }, [initialTableState, customFields])

  useEffect(() => {
    setAssetVizRenderingMap(new Map())
  }, [pagination, setAssetVizRenderingMap])

  const globalFilterFn: FilterFn<AssetData> = (row, _, filterValue) => {
    const searchValue =
      typeof filterValue === 'string' ? filterValue.toLowerCase() : filterValue

    const nameMatch = row.original.name.toLowerCase().includes(searchValue)

    const erpIdMatch =
      row.original.erpId?.toLowerCase().includes(searchValue) || false

    return nameMatch || erpIdMatch
  }

  const dateRangeFilterFn: FilterFn<unknown> = (row, columnId, filterValue) => {
    const [startDateStr, endDateStr] = filterValue as [
      string | undefined,
      string | undefined,
    ]
    const rowValue = row.getValue(columnId) as string

    if (!rowValue) return true

    const rowDate = parseISO(rowValue)
    const startDate = startDateStr ? parseISO(startDateStr) : null
    const endDate = endDateStr ? parseISO(endDateStr) : null

    if (endDate) {
      endDate.setHours(23, 59, 59, 999) // set to end of the day
    }

    const isAfterStart = startDate ? compareAsc(rowDate, startDate) >= 0 : true
    const isBeforeEnd = endDate ? compareAsc(rowDate, endDate) <= 0 : true

    return isAfterStart && isBeforeEnd
  }

  const multiSelectFilterFn: FilterFn<unknown> = (
    row,
    columnId,
    filterValue
  ) => {
    const rowValue = row.getValue(columnId)

    // Handle case where filterValue is not an array
    if (!Array.isArray(filterValue)) {
      return false
    }

    const normalizedFilterValues = filterValue.map((v: string) =>
      normalizeString(v)
    )

    const processValue = (value: string) => {
      const normalizedValue = normalizeString(value)
      return normalizedFilterValues.includes(normalizedValue)
    }

    if (typeof rowValue === 'string') {
      return processValue(rowValue)
    } else if (Array.isArray(rowValue)) {
      return rowValue.some(
        (item) => typeof item === 'string' && processValue(item)
      )
    }

    return false
  }

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    filterFns: {
      customGlobalFilter: globalFilterFn,
      dateRangeFilter: dateRangeFilterFn,
      multiSelectFilter: multiSelectFilterFn,
    },
    autoResetPageIndex: false,
  })

  const handleRowClick = (row: AssetData, event: React.MouseEvent) => {
    if (event.metaKey || event.ctrlKey) {
      const url = `/assets/${siteID}/${row.id}?view=details`
      window.open(url, '_blank')
      return
    }
    event.preventDefault()

    // Get current table state and convert filters to individual parameters
    const currentFilters = table
      .getState()
      .columnFilters.reduce(
        (acc: Record<string, string | string[] | undefined>, filter) => {
          // Check if the filter is a custom field by looking up the column definition
          const column = columns.find((col) => col.id === filter.id)
          if (column && customFields.some((field) => field.key === filter.id)) {
            // This is a custom field filter
            acc[filter.id] = filter.value as string | string[] | undefined
          } else {
            // This is a standard filter
            acc[filter.id] = filter.value as string | string[] | undefined
          }
          return acc
        },
        {}
      )

    navigate({
      to: '/assets/$siteID/$unitID',
      from: '/assets/$siteID',
      params: { siteID, unitID: row.id },
      search: (prev) => ({
        ...prev,
        view: 'details',
        ...currentFilters, // Spread all filters, including custom fields
      }),
    })
  }

  const handleFilterChange = (
    filteredData: RowModel<AssetData>,
    globalFilter: unknown,
    columnFilters: ColumnFiltersState
  ) => {
    const filters = columnFilters.reduce(
      (acc: Record<string, string | string[] | undefined>, filter) => {
        acc[filter.id] = filter.value as string | string[] | undefined
        return acc
      },
      {}
    )
    onTableStateChange?.(filters)
  }

  return (
    <TableComponent
      isLoadingData={isLoading}
      data={data}
      columns={columns}
      footerName='Asset(s)'
      sorting={sorting}
      setSorting={setSorting}
      pagination={pagination}
      setPagination={setPagination}
      onRowClick={(row, event) => {
        handleRowClick(row.original, event)
      }}
      globalFilterFn={globalFilterFn}
      onFilteredDataChange={handleFilterChange}
      urlFilters={initialTableState || {}}
    />
  )
}
